import { useEffect, useState } from "react";
import { HOLDER_LABELS, type StockDto } from "@gatiq/shared";
import { Card, EmptyState, Page, PageHeader, TD, TH, THead, TR, Table, TableSkeleton, Toolbar, ToolbarCount, useToast } from "@/ui";
import { api, ApiClientError } from "@/lib/api";

const n = (value: number): string => value.toLocaleString("en-IN");

/** Network → Stock (D-20): who holds how much of each product, and what they have sold. */
export function StockPage() {
  const toast = useToast();
  const [data, setData] = useState<StockDto | null>(null);

  useEffect(() => {
    api
      .get<StockDto>("/org/stock")
      .then(setData)
      .catch((err: unknown) => {
        toast.error(err instanceof ApiClientError ? err.message : "Could not load stock.");
        setData({ products: [], rows: [] });
      });
  }, [toast]);

  const totalInStock = data?.rows.reduce((sum, r) => sum + r.inStock, 0) ?? 0;

  return (
    <Page bleed>
      <PageHeader title="Stock" />
      <Card fill>
        <Toolbar trailing={data ? <ToolbarCount count={totalInStock} noun="unit" /> : null}>
          <p className="text-sm text-muted">In stock by holder and product. Sold means scanned by a karigar.</p>
        </Toolbar>
        {!data ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TableSkeleton cols={4} />
          </div>
        ) : data.rows.length === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <EmptyState
              message="Stock appears once reward codes are printed while dispatch is on."
              title="No stock yet"
            />
          </div>
        ) : (
          <Table fill>
            <THead>
              <TR>
                <TH>Holder</TH>
                {data.products.map((p) => (
                  <TH align="right" key={p.id}>
                    {p.name}
                  </TH>
                ))}
                <TH align="right">In stock</TH>
                <TH align="right">Sold</TH>
              </TR>
            </THead>
            <tbody>
              {data.rows.map((row) => (
                <TR key={`${row.holder.type}:${row.holder.id ?? ""}`}>
                  <TD>
                    <span className="text-ink">{row.holder.name}</span>
                    {row.holder.type !== "org" ? (
                      <span className="ml-2 text-xs text-muted">{HOLDER_LABELS[row.holder.type]}</span>
                    ) : null}
                  </TD>
                  {data.products.map((p) => (
                    <TD align="right" key={p.id}>
                      <span className="tabular-nums">{n(row.byProduct[p.id]?.inStock ?? 0)}</span>
                    </TD>
                  ))}
                  <TD align="right">
                    <span className="font-medium tabular-nums text-ink">{n(row.inStock)}</span>
                  </TD>
                  <TD align="right">
                    <span className="tabular-nums text-muted">{n(row.sold)}</span>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Page>
  );
}
